import { useMemo } from 'react';

import type { PackArchitectureContextValue } from './packArchitectureProvider.types';
import { usePackArchitectureAxisConfig } from './usePackArchitectureAxisConfig';
import { usePackArchitectureChartData } from './usePackArchitectureChartData';
import { usePackArchitectureSelection } from './usePackArchitectureSelection';

export type { PackArchitectureContextValue } from './packArchitectureProvider.types';

export function usePackArchitectureProvider(): PackArchitectureContextValue {
    const axisConfig = usePackArchitectureAxisConfig();
    const chartData = usePackArchitectureChartData(axisConfig);
    const selectionState = usePackArchitectureSelection(chartData.rows);

    return useMemo(
        () => ({
            title: axisConfig.title,
            tooltip: axisConfig.tooltip,
            tableTooltip: axisConfig.tableTooltip,
            xAxis: axisConfig.xAxis,
            setXAxis: axisConfig.setXAxis,
            yAxis: axisConfig.yAxis,
            setYAxis: axisConfig.setYAxis,
            bubbleRepresents: axisConfig.bubbleRepresents,
            setBubbleRepresents: axisConfig.setBubbleRepresents,
            bubbleSize: axisConfig.bubbleSize,
            setBubbleSize: axisConfig.setBubbleSize,
            showHeatMap: axisConfig.showHeatMap,
            setShowHeatMap: axisConfig.setShowHeatMap,
            xAxisConfig: axisConfig.xAxisConfig,
            yAxisConfig: axisConfig.yAxisConfig,
            bubbleRepresentsConfig: axisConfig.bubbleRepresentsConfig,
            bubbleSizeConfig: axisConfig.bubbleSizeConfig,
            heatMapToggle: axisConfig.heatMapToggle,
            tableKpiOptionItems: axisConfig.tableKpiOptionItems,
            selectedKpis: axisConfig.selectedKpis,
            toggleTableKpi: axisConfig.toggleTableKpi,
            tableSearchInput: axisConfig.tableSearchInput,
            setTableSearchInput: axisConfig.setTableSearchInput,
            submitTableSearch: axisConfig.submitTableSearch,
            tableSearchKeyword: axisConfig.tableSearchKeyword,
            xAxisOptionGroups: axisConfig.xAxisOptionGroups,
            yAxisOptionGroups: axisConfig.yAxisOptionGroups,
            bubbleRepresentsOptionGroups: axisConfig.bubbleRepresentsOptionGroups,
            bubbleSizeOptionGroups: axisConfig.bubbleSizeOptionGroups,
            xAxisGroupByControlsMeta: axisConfig.xAxisGroupByControlsMeta,
            xAxisKpiControlsMeta: axisConfig.xAxisKpiControlsMeta,
            xAxisKpiDataRange: chartData.xAxisKpiDataRange,
            xAxisDimensionConfig: axisConfig.xAxisDimensionConfig,
            setXAxisDimensionConfig: axisConfig.setXAxisDimensionConfig,
            xAxisKpiConfig: axisConfig.xAxisKpiConfig,
            setXAxisKpiConfig: axisConfig.setXAxisKpiConfig,
            rows: chartData.rows,
            tableRows: chartData.tableRows,
            isChartLoading: chartData.isChartLoading,
            isChartPaginatedLoading: chartData.isChartPaginatedLoading,
            isTablePaginatedLoading: chartData.isTablePaginatedLoading,
            isChartTableRefetching: chartData.isChartTableRefetching,
            isChartTableLoaderRefetching: chartData.isChartTableLoaderRefetching,
            isChartPanelLoading: chartData.isChartPanelLoading,
            isChartError: chartData.isChartError,
            hasChartData: chartData.hasChartData,
            chartError: chartData.chartError,
            fetchNextPricePackArchitecturePage: chartData.fetchNextPricePackArchitecturePage,
            fetchNextTablePage: chartData.fetchNextTablePage,
            hasMorePages: chartData.hasMorePages,
            tableHasMorePages: chartData.tableHasMorePages,
            totalCount: chartData.totalCount,
            effectiveRowCount: chartData.effectiveRowCount,
            chartRequest: axisConfig.chartRequest,
            selection: selectionState.selection,
            activeSegmentKey: selectionState.activeSegmentKey,
            selectedColumnKey: selectionState.selectedColumnKey,
            selectedBubbleKey: selectionState.selectedBubbleKey,
            toggleSegmentSelection: selectionState.toggleSegment,
            selectSegment: selectionState.selectSegment,
            selectBubble: selectionState.selectBubble,
            selectTableCell: selectionState.selectTableCell,
            clearSelection: selectionState.clearSelection,
            showNoFilterSelection: axisConfig.showNoFilterSelection,
        }),
        [axisConfig, chartData, selectionState],
    );
}
